import { useState } from 'react';
import type { BoothManifest } from '@photobooth/shared';
import { checkPassword, rememberUnlocked } from '../builder/passwordGate';
import { requestBoothPasswordReset } from '../api/client';
import { Button } from './Button';

interface Props {
  manifest: BoothManifest;
  onUnlocked(): void;
}

type Mode = 'enter' | 'reset-sent';

/**
 * Full-screen prompt shown when a booth has a password set. On a correct
 * entry, we cache "unlocked" in sessionStorage so the user only types it
 * once per session per booth. Also offers a "Forgot password?" magic-link
 * reset flow that emails the booth owner a one-time link.
 */
export function PasswordGate({ manifest, onUnlocked }: Props) {
  const [pw, setPw] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>('enter');
  const [sentTo, setSentTo] = useState<string | null>(null);

  const submit = async () => {
    if (!pw || busy) return;
    setBusy(true);
    setErr(null);
    try {
      const ok = await checkPassword(manifest, pw);
      if (ok) {
        rememberUnlocked(manifest.slug);
        onUnlocked();
      } else {
        setErr('That password doesn’t look right.');
      }
    } finally {
      setBusy(false);
    }
  };

  const requestReset = async () => {
    if (busy) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await requestBoothPasswordReset(manifest.slug);
      setSentTo(res.emailMasked);
      setMode('reset-sent');
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not send reset email.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100dvh',
        display: 'grid',
        placeItems: 'center',
        padding: 24,
        color: 'var(--text)',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 360,
          display: 'grid',
          gap: 12,
          padding: 24,
          borderRadius: 16,
          background: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        <h1 style={{ margin: 0, fontSize: 20 }}>{manifest.title}</h1>
        {mode === 'reset-sent' ? (
          <>
            <p style={{ margin: 0, color: 'var(--text-dim)', fontSize: 14 }}>
              If <strong>{sentTo}</strong> is the owner email on file, we’ve
              sent a magic link there. Open it within 15 minutes to set a new
              password.
            </p>
            <Button variant="ghost" onClick={() => setMode('enter')}>
              Back to password
            </Button>
          </>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void submit();
            }}
            style={{ display: 'grid', gap: 12 }}
          >
            <p style={{ margin: 0, color: 'var(--text-dim)', fontSize: 14 }}>
              This booth is password-protected. Enter the password to continue.
            </p>
            <input
              type="password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              autoFocus
              placeholder="Password"
              style={{
                padding: '12px 14px',
                borderRadius: 10,
                border: err ? '1px solid var(--danger)' : '1px solid rgba(255,255,255,0.18)',
                background: 'rgba(0,0,0,0.45)',
                color: 'var(--text)',
                fontSize: 15,
              }}
            />
            {err && (
              <div style={{ fontSize: 13, color: 'var(--danger)' }}>{err}</div>
            )}
            <Button variant="primary" disabled={!pw || busy} type="submit">
              {busy ? 'Checking…' : 'Unlock'}
            </Button>
            <button
              type="button"
              onClick={() => void requestReset()}
              disabled={busy}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--text-dim)',
                cursor: 'pointer',
                textDecoration: 'underline',
                padding: 0,
                fontSize: 13,
                justifySelf: 'center',
              }}
            >
              Forgot password?
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
